import React, { useEffect, useState, useCallback } from "react";
import Swal from "sweetalert2";
import {
  obtenerProveedores,
  crearProveedor,
  actualizarProveedor,
  eliminarProveedor,
  normalizarProveedor,
} from "../services/proveedoresService";
import PageHeader from "../components/PageHeader";
import TableToolbar from "../components/TableToolbar";
import DataTable from "../components/DataTable";

const INITIAL_FORM = {
  id: 0,
  clave: "",
  rfc: "",
  nombre: "",
  direccion: "",
  colonia: "",
  ciudad: "",
  estado: "",
  cp: "",
  email: "",
  estatus: "Activo",
  telefono: "",
};

/* ─── Campo — definido FUERA para evitar pérdida de foco ─────────────── */
const Campo = ({
  label,
  name,
  type = "text",
  full = false,
  value,
  onChange,
}) => (
  <div className="lmd-field" style={full ? { gridColumn: "1 / -1" } : {}}>
    <label className="lmd-label">{label}</label>
    <input
      className="lmd-input"
      type={type}
      name={name}
      value={value ?? ""}
      onChange={(e) => onChange(name, e.target.value)}
    />
  </div>
);

/* ─── Formulario del modal — estado interno propio ───────────────────── */
function ProveedorForm({ initialData, onGuardar, onCancelar }) {
  const [form, setForm] = useState(initialData);

  const setField = (name, value) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  return (
    <>
      <div className="lmd-modal-body">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "var(--sp-4)",
          }}
        >
          <Campo
            label="Clave *"
            name="clave"
            value={form.clave}
            onChange={setField}
          />
          <Campo label="RFC" name="rfc" value={form.rfc} onChange={setField} />
          <Campo
            label="Nombre / Razon Social *"
            name="nombre"
            value={form.nombre}
            onChange={setField}
            full
          />
          <Campo
            label="Direccion"
            name="direccion"
            value={form.direccion}
            onChange={setField}
            full
          />
          <Campo
            label="Colonia"
            name="colonia"
            value={form.colonia}
            onChange={setField}
          />
          <Campo
            label="Ciudad"
            name="ciudad"
            value={form.ciudad}
            onChange={setField}
          />
          <Campo
            label="Estado"
            name="estado"
            value={form.estado}
            onChange={setField}
          />
          <Campo label="CP" name="cp" value={form.cp} onChange={setField} />
          <Campo
            label="Telefono"
            name="telefono"
            value={form.telefono}
            onChange={setField}
          />
          <Campo
            label="Email"
            name="email"
            value={form.email}
            onChange={setField}
            type="email"
          />

          <div className="lmd-field">
            <label className="lmd-label">Estatus</label>
            <select
              className="lmd-select"
              style={{ width: "100%" }}
              value={form.estatus}
              onChange={(e) => setField("estatus", e.target.value)}
            >
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
            </select>
          </div>
        </div>
      </div>

      <div className="lmd-modal-footer">
        <button className="lmd-btn lmd-btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
        <button
          className="lmd-btn lmd-btn-primary"
          onClick={() => onGuardar(form)}
        >
          Guardar
        </button>
      </div>
    </>
  );
}

/* ─── Componente principal ───────────────────────────────────────────── */
export default function ProveedoresCatalogo() {
  const [proveedores, setProveedores] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [estatusFilter, setEstatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formInicial, setFormInicial] = useState(INITIAL_FORM);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await obtenerProveedores();
      const lista = resp.data.map((p) => normalizarProveedor(p));
      setProveedores(lista);
      setFiltered(lista);
    } catch (err) {
      console.error("Error al cargar proveedores", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    let lista = proveedores;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      lista = lista.filter(
        (p) =>
          (p.clave || "").toLowerCase().includes(s) ||
          (p.nombre || "").toLowerCase().includes(s) ||
          (p.rfc || "").toLowerCase().includes(s) ||
          (p.ciudad || "").toLowerCase().includes(s),
      );
    }
    if (estatusFilter.trim()) {
      lista = lista.filter(
        (p) => (p.estatus || "").toLowerCase() === estatusFilter.toLowerCase(),
      );
    }
    setFiltered(lista);
    setPage(1);
  }, [search, estatusFilter, proveedores]);

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize) || 1;

  const abrirNuevo = () => {
    setEditMode(false);
    setFormInicial(INITIAL_FORM);
    setModalOpen(true);
  };
  const abrirEditar = (p) => {
    setEditMode(true);
    setFormInicial(p);
    setModalOpen(true);
  };

  const guardar = async (form) => {
    if (!form.clave || !form.nombre) {
      Swal.fire("Atencion", "Clave y Nombre son obligatorios", "warning");
      return;
    }
    try {
      if (!editMode) {
        await crearProveedor(form);
      } else {
        await actualizarProveedor(form.id, form);
      }
      Swal.fire({
        icon: "success",
        title: "Guardado",
        timer: 1800,
        showConfirmButton: false,
      });
      setModalOpen(false);
      cargarDatos();
    } catch (err) {
      const msg = err.response?.data || "No se pudo guardar";
      Swal.fire(
        "Error",
        typeof msg === "string" ? msg : "Error en servidor",
        "error",
      );
    }
  };

  const borrar = async (id) => {
    const resp = await Swal.fire({
      title: "Eliminar proveedor",
      text: `ID ${id}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    });
    if (!resp.isConfirmed) return;
    try {
      await eliminarProveedor(id);
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        timer: 1500,
        showConfirmButton: false,
      });
      cargarDatos();
    } catch {
      Swal.fire("Error", "No se pudo eliminar", "error");
    }
  };

  const columns = [
    { key: "clave", title: "Clave", style: { width: 110 } },
    { key: "nombre", title: "Nombre / Razon Social" },
    { key: "rfc", title: "RFC", style: { width: 140 } },
    { key: "ciudad", title: "Ciudad", style: { width: 130 } },
    { key: "telefono", title: "Telefono", style: { width: 130 } },
    {
      key: "estatus",
      title: "Estatus",
      style: { width: 100 },
      render: (r) => (
        <span
          className={`lmd-badge ${r.estatus === "Activo" ? "lmd-badge-success" : "lmd-badge-neutral"}`}
        >
          {r.estatus}
        </span>
      ),
    },
    {
      key: "acciones",
      title: "",
      style: { width: 140 },
      render: (r) => (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            className="lmd-btn lmd-btn-warning lmd-btn-sm"
            onClick={() => abrirEditar(r)}
          >
            Editar
          </button>
          <button
            className="lmd-btn lmd-btn-danger  lmd-btn-sm"
            onClick={() => borrar(r.id)}
          >
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  const extraRight = (
    <select
      className="lmd-select"
      value={estatusFilter}
      onChange={(e) => setEstatusFilter(e.target.value)}
    >
      <option value="">Todos</option>
      <option value="Activo">Activo</option>
      <option value="Inactivo">Inactivo</option>
    </select>
  );

  return (
    <>
      <PageHeader
        title="Catalogo de Proveedores"
        subtitle="Alta, edicion y consulta de proveedores"
      />

      <div className="lmd-card">
        <TableToolbar
          searchPlaceholder="Buscar por clave, nombre, RFC o ciudad..."
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={() => {}}
          sizeValue={pageSize}
          onSizeChange={(n) => {
            setPageSize(n);
            setPage(1);
          }}
          onAdd={abrirNuevo}
          addText="Nuevo Proveedor"
          extraRight={extraRight}
        />

        {loading && (
          <div className="lmd-loading-bar">
            <div className="lmd-spinner" />
            Cargando...
          </div>
        )}

        <DataTable columns={columns} rows={paginated} keyField="id" />

        <div className="lmd-pagination">
          <span className="lmd-pagination-info">
            Total: <strong>{filtered.length}</strong> proveedores
          </span>
          <div className="lmd-pagination-spacer" />
          <button
            className="lmd-page-btn"
            disabled={page <= 1}
            onClick={() => setPage(1)}
          >
            «
          </button>
          <button
            className="lmd-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ‹
          </button>
          <span
            style={{
              padding: "0 10px",
              fontSize: "var(--fs-sm)",
              color: "var(--clr-text-secondary)",
            }}
          >
            Pag. <strong>{page}</strong> / <strong>{totalPages}</strong>
          </span>
          <button
            className="lmd-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </button>
          <button
            className="lmd-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage(totalPages)}
          >
            »
          </button>
        </div>
      </div>

      {/* Modal — key reinicia el estado interno al abrir */}
      {modalOpen && (
        <div className="lmd-modal-overlay" onClick={() => setModalOpen(false)}>
          <div
            className="lmd-modal lmd-modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lmd-modal-header">
              <h3 className="lmd-modal-title">
                {editMode
                  ? `Editar Proveedor — ${formInicial.clave}`
                  : "Nuevo Proveedor"}
              </h3>
              <button
                className="lmd-modal-close"
                onClick={() => setModalOpen(false)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <ProveedorForm
              key={formInicial.id}
              initialData={formInicial}
              onGuardar={guardar}
              onCancelar={() => setModalOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}
